import { valueOf } from "./articleHelper"
import {
    ProductsFilter,
    ProductGroupFilter,
    CharacteristicFilter,
    BoolFilter
} from "./filter"
import { isProduct } from "./guards"
import { CharacteristicFilterRequest } from "./types/request"
import { Product, Interval, ProductResult } from "./types/response"

export * from "./filter/boolFilter"
export * from "./filter/characteristicFilter"
export * from "./filter/productGroupFilter"
export * from "./filter/productsFilter"

export function matchFilter(filter: Filter, product: Product) {
    if (filter instanceof ProductsFilter) {
        return matchProductsFilter(filter, product)
    } else if (filter instanceof ProductGroupFilter) {
        return matchProductGroupFilter()
    } else if (filter instanceof CharacteristicFilter) {
        return matchCharacteristicFilter(filter, product)
    } else if (filter instanceof BoolFilter) {
        return matchBoolFilter(filter, product)
    }

    return false
}

/**
 * Test against a list of products
 * @param {ProductsFilter} filter
 * @param {Object} product
 * @returns {Boolean}
 */
function matchProductsFilter(
    filter: ProductsFilter,
    product: Product | ProductResult
) {
    const productsFilter = filter.build().productsFilter
    const whitelist = productsFilter ? productsFilter.ids : []
    return whitelist.some((id) =>
        !isProduct(product) ? product.product.id === id : false
    )
}

/**
 * Can't check product groups at runtime.
 * @param {ProductsFilter} filter
 * @param {Object} product
 * @returns {Boolean} true
 */
function matchProductGroupFilter(): true {
    return true
}

function matchPlainValue(filter: CharacteristicFilterRequest, value: any) {
    if (filter.eq) {
        return value === filter.eq
    } else if (filter.ne) {
        return value !== filter.ne
    } else if (filter.gt) {
        if (typeof value === "number") {
            return value > filter.gt
        }
    } else if (filter.gte) {
        if (typeof value === "number") {
            return value >= filter.gte
        }
    } else if (filter.lt) {
        if (typeof value === "number") {
            return value < filter.lt
        }
    } else if (filter.lte) {
        if (typeof value === "number") {
            return value <= filter.lte
        }
    }

    return false
}

function matchRangeValue(filter: any, range: Interval): boolean {
    if (filter.eq) {
        return (
            range.lowerEndpoint <= filter.eq && range.upperEndpoint >= filter.eq
        )
    } else if (filter.ne) {
        return (
            range.lowerEndpoint > filter.eq || range.upperEndpoint < filter.eq
        )
    } else if (filter.gt) {
        return range.upperEndpoint > filter.gt
    } else if (filter.gte) {
        return range.upperEndpoint >= filter.gte
    } else if (filter.lt) {
        return range.lowerEndpoint < filter.lt
    } else if (filter.lte) {
        return range.lowerEndpoint <= filter.lte
    }

    return false
}

/**
 * Matches a value. Can handle arrays, plain, and range values.
 * @param {*} filter the catalog rest filter
 * @param {*} value the product value
 * @returns {Boolean}
 */
function matchValue(filter: CharacteristicFilterRequest, value: any) {
    if (!value) {
        return false
    }

    if (value.interval === true) {
        return matchRangeValue(filter, value.value)
    }

    return matchPlainValue(
        filter,
        Object.prototype.hasOwnProperty.call(value, "value")
            ? value.value
            : value
    )
}

/**
 * Test characteristics
 * @param {CharacteristicsFilter} filter
 * @param {Object} product
 * @returns {Boolean}
 */
function matchCharacteristicFilter(
    filter: CharacteristicFilter,
    product: Product
) {
    const cf = filter.build().characteristicFilter

    if (!cf || !cf.id) {
        return false
    }

    const productValue = valueOf(product, cf.id)

    if (!productValue) {
        return false
    }

    if (Array.isArray(productValue)) {
        return productValue.some((value) => matchValue(cf, value))
    }

    return matchValue(cf, productValue)
}

/**
 * Test combinations
 * @param {BoolFilter} filter
 * @param {Object} product
 * @returns {Boolean}
 */
function matchBoolFilter(filter: BoolFilter, product: any): boolean {
    const must = filter.filter.must
    const should = filter.filter.should
    if (must && must.length > 0) {
        if (!must.every((filter) => matchFilter(filter, product))) {
            return false
        }
    }
    if (should && should.length > 0) {
        return should.some((filter) => matchFilter(filter, product))
    }

    return true
}

/**
 * A filter factory.
 */
export class Filter {
    /**
     * Create a boolean filter.
     * @see BoolFilter
     */
    static boolFilter(): BoolFilter {
        return new BoolFilter()
    }

    /**
     * Create a characteristic filter.
     * @see CharacteristicFilter
     */
    static characteristicFilter(): CharacteristicFilter {
        return new CharacteristicFilter()
    }

    /**
     * Create a product group filter.
     * @see ProductGroupFilter
     */
    static productGroupFilter(): ProductGroupFilter {
        return new ProductGroupFilter()
    }

    /**
     * Create a products filter.
     * @see ProductsFilter
     */
    static productsFilter(): ProductsFilter {
        return new ProductsFilter()
    }
}
